import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Draggable from 'react-draggable';

import QuestionDisplayContainer from '../question/QuestionDisplay';
import SectionQuestionsDisplayContainer from '../sectionview/SectionQuestionsDisplay';
import StimulusDisplay from '../stimulusDisplay/StimulusDisplay';

import assessmentReader from '../../store/readHelpers/AssessmentReader';
import i18nService, { t } from '../../services/i18nService';
import LoadTracker from '../../services/LoadTracker';
import marketplaceSettingsService from '../../services/MarketplaceSettingsService';
import viewReadyTracker from '../../services/ViewReadyTracker';

import styles from './DisplayTypeContainer.scss';
import utilService from '../../services/UtilService';
import SectionRubricBlock from '../../themes/components/fjord/SectionRubricBlock';
import dragAdjustmentService from '../../services/DragAdjustmentService';
import UIConstants from '../../constants/UIConstants';
import AnswerKeys from '../answerKeys/AnswerKeys';
import QuestionRubricBlockWithTitle from '../question/QuestionRubricBlockWithTitle';
import displayTypeContainerSelector from '../../selectors/displayTypeWrapper/DisplayTypeContainerSelector';
import sessionReader from '../../store/readHelpers/SessionReader';
import storeManager from '../../store/StoreManager';
import uiActions from '../../actions/UIActions';
import uiService from '../../services/UIService';
import scorableItemNumberingService from '../../services/questionView/ScorableItemNumberingService';

let windowResizeTimeout;

export class DisplayTypeWrapper extends React.Component {
    static propTypes = {
        currentlyShown: PropTypes.object,
        currentQuestion: PropTypes.object,
        isQuestionPreviewMode: PropTypes.bool.isRequired,
        isSplitQuestionView: PropTypes.bool.isRequired,
        isStimulusResizingAllowed: PropTypes.bool.isRequired,
        isTopStimulus: PropTypes.bool.isRequired,
        regionAccessibilityAttributes: PropTypes.object.isRequired,
        response: PropTypes.object.isRequired,
        section: PropTypes.object,
        session: PropTypes.object.isRequired,
        settings: PropTypes.object.isRequired,
        shouldAddSectionAriaAttributes: PropTypes.bool.isRequired,
        shouldAdjustSectionPaddings: PropTypes.bool.isRequired,
        shouldRenderAnswerKeys: PropTypes.bool.isRequired,
        showQuestionCopyrightText: PropTypes.bool.isRequired,
        showQuestionRubricBlockOnSectionLevel: PropTypes.bool.isRequired,
        showQuestionRubricBlockOnTopLevel: PropTypes.bool.isRequired,
        showSectionRubricBlockOnSectionLevel: PropTypes.bool.isRequired,
        showSectionRubricBlockOnTopLevel: PropTypes.bool.isRequired,
        showStimulus: PropTypes.bool.isRequired,
        showUpperLevelQuestionRubricBlock: PropTypes.bool.isRequired,
        stimulusContent: PropTypes.object,
        stimulusHolder: PropTypes.string,
        stimulusPosition: PropTypes.string,
        ui: PropTypes.object,
        verticalDisplay: PropTypes.bool.isRequired,
    };

    constructor(props) {
        super(props);
        const { section, showStimulus, stimulusPosition, isTopStimulus } = this.props;
        this.isTouchDevice = utilService.isTouchDevice();
        let stimulusSize;

        if (showStimulus) {
            this.inlineStimulus = false;
            this.stimulusPosition = '';
            this.startStimulusSize = this.getStartStimulusSize(section);
            this.stimulusPosition = stimulusPosition;
            this.inlineStimulus = isTopStimulus && !marketplaceSettingsService.useFixedTopStimulus();
            stimulusSize = isTopStimulus
                ? (window.innerHeight / 2) - 60
                : Math.max(0, ((window.innerWidth / (100 / this.startStimulusSize)) - 32)); // 32px padding in stimulus
        }

        // The following prevents scrolling on resizing stimulus
        this.scrollable = true;
        this.listener = (e) => {
            if (!this.scrollable) {
                e.preventDefault();
            }
        };
        document.addEventListener('touchmove', this.listener, { passive: false });
        this.handleWindowResize = this.handleWindowResize.bind(this);
        this.dragStop = this.dragStop.bind(this);
        this.state = {
            stimulusSize,
            containerStyle: {},
            dragging: false,
        };
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillMount() {
        this.reportReady = viewReadyTracker.getReporter();
        this.initLoadTracker(this.props.verticalDisplay, this.props.showStimulus);
    }

    componentDidMount() {
        const { showStimulus } = this.props;
        if (showStimulus && !this.inlineStimulus) {
            this.stimulusSizeOnDragStop = undefined;
            this.setStimulusSize();
        }
        window.addEventListener('resize', this.handleWindowResize);

        const { section } = this.props;
        if (assessmentReader.hasMatchQuestions(section)) {
            dragAdjustmentService.init();
        }

        setTimeout(() => {
            this.calculateContainerStyle();
        }, 50);
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillUpdate(nextProps) {
        const { currentlyShown, section, showStimulus } = this.props;
        const sectionChanged = section.id !== nextProps.section.id;
        const currentShownSectionChanged = currentlyShown.sectionId !== nextProps.currentlyShown.sectionId;
        const currentShownQuestionChanged = currentlyShown.questionId !== nextProps.currentlyShown.questionId;
        const showStimulusChanged = showStimulus !== nextProps.showStimulus;

        if (nextProps.section && (currentShownSectionChanged || currentShownQuestionChanged)) {
            this.reportReady = viewReadyTracker.getReporter();
            const isStimulusContentUpdated = nextProps.showStimulus && (sectionChanged || !showStimulus);
            this.initLoadTracker(nextProps.verticalDisplay, isStimulusContentUpdated);
        }

        if ((nextProps.section && sectionChanged) || showStimulusChanged) {
            this.stimulusSizeOnDragStop = undefined;
            this.conditionallySetStimulusSize(nextProps);
        }
    }

    componentDidUpdate(prevProps) {
        const { section } = this.props;
        const contentUpdated = prevProps.section?.questions !== section?.questions ||
            prevProps.section?.stimulusContent !== section?.stimulusContent;

        if (contentUpdated) {
            if (assessmentReader.hasMatchQuestions(section)) {
                dragAdjustmentService.init();
            } else {
                dragAdjustmentService.destroy();
            }
        }

        if ((prevProps.session.hypothesisSidebarOpen !== this.props.session.hypothesisSidebarOpen)
            || (prevProps.session.annotationsSidebarOpen !== this.props.session.annotationsSidebarOpen)) {
            this.setStimulusSize();
        }

        if ((prevProps.showStimulus !== this.props.showStimulus) || (prevProps.isSplitQuestionView !== this.props.isSplitQuestionView)) {
            setTimeout(() => {
                this.calculateContainerStyle();
            }, 50);
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleWindowResize);
        clearTimeout(windowResizeTimeout);
        windowResizeTimeout = undefined;
        dragAdjustmentService.destroy();
    }

    getStartStimulusSize = (section) => {
        const { DEFAULT_STIMULUS_SIZE = 50 } = window.marketplaceProperties;
        if (isNaN(section.stimulusSize)) {
            return DEFAULT_STIMULUS_SIZE;
        }
        return section.stimulusSize;
    };

    handleWindowResize() {
        clearTimeout(windowResizeTimeout);
        windowResizeTimeout = undefined;
        windowResizeTimeout = setTimeout(() => {
            if (this.props.showStimulus && !this.inlineStimulus) {
                this.conditionallySetStimulusSize(this.props);
            }
            this.calculateContainerStyle();
        }, 50);
    }

    conditionallySetStimulusSize = (props) => {
        const { showStimulus, stimulusPosition, isTopStimulus, section } = props;

        if (!showStimulus) {
            return;
        }

        this.stimulusPosition = stimulusPosition;
        this.inlineStimulus = isTopStimulus && !marketplaceSettingsService.useFixedTopStimulus();

        if (this.inlineStimulus) {
            return;
        }

        this.startStimulusSize = this.stimulusSizeOnDragStop ? this.stimulusSizeOnDragStop : this.getStartStimulusSize(section);
        this.setStimulusSize();
    }

    getHypothesisSidebarSize = () => {
        const hypothesis = document.querySelector('.h-sidebar-iframe');
        if (hypothesis) {
            return hypothesis.contentWindow.innerWidth;
        }
        return 0;
    };

    setStimulusSize = () => {
        if (this.stimulusPosition === 'top') {
            this.setState({
                stimulusSize: undefined,
            }, () => {
                setTimeout(() => {
                    this.setStimulusSizeState();
                }, 50);
            });
        } else {
            this.setStimulusSizeState();
        }
    };

    setStimulusSizeState = () => {
        const stimulusSize = this.calculateStimulusSize();
        this.setState({
            stimulusSize,
        });
    };

    calculateStimulusSize = () => {
        const { session } = this.props;
        if (this.stimulusPosition === 'top') {
            const { marginTop, marginBottom } = getComputedStyle(this.sectionStimulus.stimulus);
            const stimulusMarginTop = parseInt(marginTop, 10);
            const stimulusMarginBottom = parseInt(marginBottom, 10);
            const stimulusHeight = this.sectionStimulus.stimulus.clientHeight;
            const totalHeight = stimulusMarginTop + stimulusMarginBottom + stimulusHeight;
            if (totalHeight < (window.innerHeight / 2) - 60) {
                return totalHeight;
            }
            return (window.innerHeight / 2) - 58; // 58px is the height of the footer
        }

        const mainScreenContent = document.getElementById('main-screen-content');
        let width = mainScreenContent.offsetWidth;
        const stimulusSize = this.stimulusSizeOnDragStop || this.startStimulusSize;

        if (session.hypothesisSidebarOpen) {
            width -= this.getHypothesisSidebarSize();
        }

        return Math.max(0, ((width / (100 / stimulusSize)) - 32)); // 32px padding in stimulus
    };

    calculateContainerStyle = () => {
        const {
            showStimulus,
            isSplitQuestionView,
            session,
        } = this.props;

        const containerStyle = {};

        if (showStimulus || isSplitQuestionView) {
            const offsetHeightSum = this.calculateOffsetHeight();
            // dynamically calculate max height for container depends on offestHeightSum
            containerStyle.maxHeight = `calc(100vh - ${offsetHeightSum}px)`;
        }

        if (session.hypothesisSidebarOpen) {
            containerStyle.width = `calc(${window.innerWidth - this.getHypothesisSidebarSize()}px - 3rem)`;
        }

        if (!utilService.isDeepEqual(containerStyle, this.state.containerStyle)) {
            this.setState({
                containerStyle,
            });
        }
    }

    initLoadTracker = (verticalDisplay, isStimulusContentUpdated = false) => {
        this.loadTracker = new LoadTracker((success) => {
            this.reportReady(success);
        }, 'DisplayTypeContainer');

        if (isStimulusContentUpdated) {
            this.stimulusTracker = this.loadTracker.addItemToTrack('StimulusDisplay');
        }

        if (verticalDisplay) {
            this.sectionTracker = this.loadTracker.addItemToTrack('SectionQuestionsDisplayContainer');
        } else {
            this.questionTracker = this.loadTracker.addItemToTrack('QuestionDisplayContainer');
        }

        this.loadTracker.setAllItemsAdded();
    };

    handleDragAction = (e, data) => {
        const { stimulusSize } = this.state;
        const isTopStimulus = this.stimulusPosition === 'top';
        const offset = isTopStimulus ? data.deltaY : data.deltaX;
        this.scrollable = false;

        let newStimulusSize = stimulusSize + offset;
        if (!isTopStimulus && newStimulusSize < 2) {
            newStimulusSize = 2;
        } else if (isTopStimulus && newStimulusSize < 16) {
            newStimulusSize = 16;
        } else if (isTopStimulus && newStimulusSize > window.innerHeight) {
            newStimulusSize = window.innerHeight;
        } else if (newStimulusSize > (window.innerWidth - 36)) {
            newStimulusSize = window.innerWidth - 36;
        }
        this.setState({
            stimulusSize: newStimulusSize,
        });
    };

    dragStop = () => {
        this.scrollable = true;
        this.stimulusSizeOnDragStop = (this.state.stimulusSize / (window.innerWidth - 64)) * 100;
        storeManager.dispatch(uiActions.setStimulusState(UIConstants.STIMULUS_STATE.DROP));
        this.setState({
            dragging: false,
        });
    };

    dragStart = () => {
        this.setState({
            dragging: true,
        });
        storeManager.dispatch(uiActions.setStimulusState(UIConstants.STIMULUS_STATE.DRAG));
    };

    calculateOffsetHeight = () => {
        const footerElement = document.getElementsByTagName('footer')[0];
        const headerElement = document.getElementById('headerTopBar');
        const rubricTopLevel = document.getElementById('rubric__top-level');

        const offsetsArray = [
            footerElement?.offsetHeight,
            headerElement?.offsetHeight,
            rubricTopLevel?.offsetHeight,
        ];

        // 455 - this is the height that is applied for css media query (zoomL)
        // 72 - this is padding 4.5rem in css for zoomM and zoomL
        const defaultOffset = !sessionReader.isDataSetTypeQuestionSet()
            && document.body.clientHeight <= 455 ? -72 : 0;
        return offsetsArray.reduce((acc, offsetHeight) => {
            let summValue = acc;

            if (offsetHeight) {
                summValue += offsetHeight;
            }

            return summValue;
        }, defaultOffset);
    }

    getAriaLabel = () => {
        const {
            currentlyShown,
            section,
            stimulusHolder,
        } = this.props;

        return uiService.isQuestionStimulusHolder(stimulusHolder)
            ? scorableItemNumberingService.getQuestionTitle(currentlyShown.questionId)
            : `${t('generic.sectionNumberPrefix')} ${section.title}`;
    };

    render() {
        const {
            currentlyShown,
            currentQuestion,
            isQuestionPreviewMode,
            isSplitQuestionView,
            isStimulusResizingAllowed,
            regionAccessibilityAttributes,
            response,
            section,
            session,
            settings,
            shouldAddSectionAriaAttributes,
            shouldAdjustSectionPaddings,
            shouldRenderAnswerKeys,
            showQuestionCopyrightText,
            showQuestionRubricBlockOnSectionLevel,
            showQuestionRubricBlockOnTopLevel,
            showSectionRubricBlockOnSectionLevel,
            showSectionRubricBlockOnTopLevel,
            showStimulus,
            showUpperLevelQuestionRubricBlock,
            stimulusContent,
            stimulusHolder,
            ui,
            verticalDisplay,
        } = this.props;
        const { dragging, stimulusSize } = this.state;

        if (shouldRenderAnswerKeys) {
            return <AnswerKeys />;
        }

        if (!section) {
            return null;
        }

        let stimulusStyle;
        if (showStimulus) {
            stimulusStyle = this.stimulusPosition === 'top' ? { height: stimulusSize } : { width: stimulusSize };
        }

        const layoutClassName = showStimulus ? `${this.stimulusPosition}-stimulus` : '';
        const sectionNumber = section.questions && section.questions.length ? section.questions[0].sectionNumber : null;

        const containerClassList = [styles.displayTypeContainer];
        if (showStimulus || isSplitQuestionView) {
            containerClassList.push(layoutClassName);
        }
        if (showUpperLevelQuestionRubricBlock) {
            containerClassList.push('has-section-level-dynamic-rubric');
        }
        if (isQuestionPreviewMode) {
            containerClassList.push(styles.questionPreview);
        }

        const sectionContentClassList = [styles.sectionContent, UIConstants.GAP_MATCH_SCROLL_CLASS];
        if (shouldAdjustSectionPaddings) {
            sectionContentClassList.push('contains-split-question-view');
        }

        return (
            <React.Fragment>
                { showSectionRubricBlockOnTopLevel && <SectionRubricBlock section={section} ui={ui} /> }

                { showQuestionRubricBlockOnTopLevel &&
                    <div className={styles.upperLevelQuestionDynamicRubricBlock} id="rubric__top-level">
                        <QuestionRubricBlockWithTitle question={currentQuestion} section={section} />
                    </div>
                }

                <div className={containerClassList.join(' ')} style={this.state.containerStyle}>
                    { showStimulus && !this.inlineStimulus &&
                        <React.Fragment>
                            <StimulusDisplay
                                ref={(sectionStimulus) => { this.sectionStimulus = sectionStimulus; }}
                                currentlyShown={currentlyShown}
                                itemResult={response[stimulusContent.contentRevisionId]}
                                layoutClassName={layoutClassName}
                                onReady={this.stimulusTracker}
                                sectionId={section.id}
                                sectionNumber={sectionNumber}
                                sectionTitle={section.title}
                                session={session}
                                settings={settings}
                                stimulusContent={stimulusContent}
                                stimulusHolder={stimulusHolder}
                                style={stimulusStyle}
                                ui={ui}
                            />
                            { isStimulusResizingAllowed &&
                                <Draggable
                                    axis="none"
                                    onDrag={this.handleDragAction}
                                    onStop={this.dragStop}
                                    onStart={this.dragStart}
                                >
                                    <div
                                        className={
                                            `${styles.divider} ${this.isTouchDevice ? ' touch-device' : ''} ${dragging ? 'dragging' : ''}`
                                        }
                                    >
                                        <div className={styles.resizeBar}>
                                            <div className={styles.resizeBtn}>
                                                <i
                                                    className={`fa fa-arrows-${this.stimulusPosition === 'top' ? 'v' : 'h'}`}
                                                    aria-hidden="true"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </Draggable>
                            }
                        </React.Fragment>
                    }

                    <div
                        className={sectionContentClassList.join(' ')}
                        id="sectionContent"
                        {...regionAccessibilityAttributes}
                    >
                        { shouldAddSectionAriaAttributes &&
                            <span className="sr-only">
                                { this.getAriaLabel() }
                            </span>
                        }

                        { showStimulus && this.inlineStimulus &&
                            <StimulusDisplay
                                currentlyShown={currentlyShown}
                                itemResult={response[stimulusContent.contentRevisionId]}
                                layoutClassName={layoutClassName}
                                onReady={this.stimulusTracker}
                                sectionId={section.id}
                                sectionNumber={sectionNumber}
                                session={session}
                                settings={settings}
                                stimulusContent={stimulusContent}
                                stimulusHolder={stimulusHolder}
                                ui={ui}
                            />
                        }

                        { showSectionRubricBlockOnSectionLevel &&
                            <SectionRubricBlock section={section} ui={ui} />
                        }

                        { showQuestionRubricBlockOnSectionLevel &&
                            <div className={styles.upperLevelQuestionDynamicRubricBlock}>
                                <QuestionRubricBlockWithTitle question={currentQuestion} section={section} isSectionLevel />
                            </div>
                        }

                        { verticalDisplay
                            ? <SectionQuestionsDisplayContainer
                                onReady={this.sectionTracker}
                                isQuestionRubricBlockShown={showUpperLevelQuestionRubricBlock}
                            />
                            : <QuestionDisplayContainer
                                onReady={this.questionTracker}
                                isQuestionRubricBlockShown={showUpperLevelQuestionRubricBlock}
                            />
                        }

                        { showQuestionCopyrightText && layoutClassName !== 'left-stimulus' &&
                            <div className={styles.copyright}>
                                {i18nService.translate(`question.copyright.${ui.marketplaceTheme}`)}
                            </div>
                        }
                    </div>
                </div>
            </React.Fragment>
        );
    }
}

class ErrorBuffer extends React.Component {
    static propTypes = {
        error: PropTypes.bool,
    };

    render() {
        if (this.props.error) {
            return null;
        }

        return <DisplayTypeWrapper {...this.props} />;
    }
}

const mapStateToProps = (state) => {
    try {
        return displayTypeContainerSelector.selectProps(state);
    } catch (error) {
        window.console.warn('Entering DisplayTypeContainer with invalid data', error);

        return { error: true };
    }
};

const DisplayTypeContainer = connect(mapStateToProps)(ErrorBuffer);
export default DisplayTypeContainer;
